<template>
  <v-container fluid class="sci-page-wrapper pa-0" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-5xl mx-auto">
        
        <!-- HEADER -->
      <div class="sci-page-header pb-4">
          <div>
            <button 
              @click="$router.push(`/biddings-manager/edit/${data.bidding_id}`)"
              class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-all mb-2 group"
            >
              <v-icon small color="currentColor" class="transition-transform group-hover:-translate-x-1">mdi-arrow-left</v-icon>
              <span class="text-[10px] font-black uppercase tracking-[0.2em]">Voltar</span>
            </button>
            <h1 class="sci-page-title-only">Item da Licitação</h1>
            <p class="text-sm text-slate-500 font-medium mt-1">{{ id ? 'Editando informações do item' : 'Cadastrando novo item/proposta na licitação' }}</p>
          </div>
        </div>

        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="space-y-6">
            
            <!-- SEÇÃO: DADOS DO ITEM -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-cube-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO E TIPO</h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <!-- Número -->
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Número</label>
                    <v-text-field
                      v-model="data.item_number"
                      v-mask-number
                      placeholder="000"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <!-- Tipo (Combustível) -->
                  <div class="md:col-span-3">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block text-orange-600">Tipo / Combustível</label>
                    <v-autocomplete
                      v-model="data.fuel_id"
                      :items="fuels"
                      item-value="fuel_id"
                      item-text="fuel_desc"
                      :rules="fieldRules"
                      placeholder="SELECIONE O TIPO..."
                      solo flat dense hide-details
                      class="custom-select"
                      append-icon="mdi-plus"
                      @click:append="$router.push('/fuels-types')"
                    />
                  </div>
                  <!-- Descrição -->
                  <div class="md:col-span-4">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Descrição do Item</label>
                    <v-autocomplete
                      v-model="data.item_description_id"
                      :items="bidding_item_descriptions"
                      item-value="item_description_id"
                      item-text="item_description_desc"
                      :rules="fieldRules"
                      placeholder="PESQUISAR DESCRIÇÃO..."
                      solo flat dense hide-details
                      class="custom-select"
                      append-icon="mdi-plus"
                      @click:append="$router.push('/biddings-items/descriptions')"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- SEÇÃO: PARTICIPANTE E MARCA -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-account-group-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">PARTICIPANTE E PRODUTO</h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Fornecedor -->
                  <div class="md:col-span-2">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Fornecedor Participante</label>
                    <v-autocomplete
                      v-model="data.provider_id"
                      :items="providers"
                      item-value="provider_id"
                      item-text="provider_name"
                      :rules="fieldRules"
                      placeholder="SELECIONE O FORNECEDOR..."
                      solo flat dense hide-details
                      class="custom-select"
                      append-icon="mdi-plus"
                      @click:append="$router.push('/providers-manager/create')"
                    />
                  </div>
                  <!-- Marca -->
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Marca</label>
                    <v-autocomplete
                      v-model="data.item_brand_id"
                      :items="bidding_item_brands"
                      item-value="item_brand_id"
                      item-text="item_brand_desc"
                      :rules="fieldRules"
                      placeholder="SELECIONE A MARCA..."
                      solo flat dense hide-details
                      class="custom-select"
                      append-icon="mdi-plus"
                      @click:append="$router.push('/biddings-items/brands')"
                    />
                  </div>
                  <!-- Tipo Estoque -->
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Tipo de Estoque</label>
                    <v-autocomplete
                      v-model="data.stock_item_id"
                      :items="bidding_stock_items"
                      item-value="stock_item_id"
                      item-text="stock_item_desc"
                      :rules="fieldRules"
                      placeholder="TIPO ESTOQUE..."
                      solo flat dense hide-details
                      class="custom-select"
                      append-icon="mdi-plus"
                      @click:append="$router.push('/biddings-items/stock-types')"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- SEÇÃO: VALORES E STATUS -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-currency-usd</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">VALORES E QUANTIDADES</h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <!-- Preço -->
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Preço Unitário</label>
                    <v-text-field
                      v-model="data.item_price"
                      v-mask-decimal.br="3"
                      @blur="calcValue()"
                      placeholder="0,000"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <!-- Quantidade -->
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block text-orange-600">Quantidade Total</label>
                    <v-text-field
                      v-model="data.item_amount"
                      v-mask-decimal.br="3"
                      @blur="calcValue()"
                      placeholder="0,000"
                      solo flat dense hide-details
                      class="custom-select font-bold"
                    />
                  </div>
                   <!-- Disponível -->
                   <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Quantidade Disponível</label>
                    <v-text-field
                      v-model="data.item_available"
                      v-mask-decimal.br="3"
                      placeholder="0,000"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>

                  <!-- Status -->
                  <div class="md:col-span-3 grid grid-cols-2 gap-4">
                    <div class="bg-slate-50 p-4 rounded-lg border border-slate-100 flex items-center justify-between">
                       <span class="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Status Ativo</span>
                       <v-switch v-model="data.item_status" color="orange" hide-details class="mt-0 pt-0"></v-switch>
                    </div>
                    <div class="bg-slate-50 p-4 rounded-lg border border-slate-100 flex items-center justify-between">
                       <span class="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Status Retroativo</span>
                       <v-switch v-model="data.item_status_retroactive" color="orange" hide-details class="mt-0 pt-0"></v-switch>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- BOTÕES DE AÇÃO -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="bg-slate-50 px-8 py-5 flex justify-end items-center gap-4">
                <button 
                  @click="$router.push(`/biddings-manager/edit/${data.bidding_id}`)"
                  class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                >
                  Cancelar
                </button>
                <div class="flex-none">
                  <button
                    @click="validate"
                    :disabled="!valid || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                  >
                    <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                    <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                    <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
                  </button>
                </div>
              </div>
            </section>

          </div>
        </v-form>
      </div>
    </div>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar multi-line border="left" :color="snack.color" v-model="snack.show" transition="scroll-y-reverse-transition" class="mb-6">
      <div class="flex items-center gap-3">
        <v-icon color="white">mdi-information-outline</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider text-white">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import helpers from "../../../mixins/helpers";
export default {
  props: ["b_id", "id"],
  mixins: [helpers],
  data: () => ({
    valid: false,
    loader: false,
    fuels: [],
    bidding_item_descriptions: [],
    providers: [],
    bidding_item_brands: [],
    bidding_stock_items: [],

    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      item_id: null,
      // item_type_id: null,
      item_description_id: null,
      provider_id: null,
      item_brand_id: null,
      stock_item_id: null,
      item_price: 0,
      item_amount: 0,
      item_billing_price: 0,
      item_measurement: 0,
      item_available: 0,
      item_total_bidding_price: 0,
      item_depreciation_year: 0,
      item_price_billing_depreciation: 0,
      item_obs: "",
      item_status_retroactive: false,
      item_status: false,
      bidding_id: null,
      prefecture_id: null,
      item_number: null,
    },
  }),
  created() {
    this.helpers;
    this.$http.get(`/providers`).then((r) => (this.providers = r.data));
    this.$http
      .get(`/biddings/${this.b_id}`)
      .then((r) => (this.data.prefecture_id = r.data[0].prefecture_id));
    this.$http.get(`/settings/fuels`).then((r) => (this.fuels = r.data));
    this.$http
      .get(`/biddings/items/descriptions`)
      .then((r) => (this.bidding_item_descriptions = r.data));
    this.$http
      .get(`/biddings/items/brands`)
      .then((r) => (this.bidding_item_brands = r.data));
    this.$http
      .get(`/biddings/items/stock_types`)
      .then((r) => (this.bidding_stock_items = r.data));

    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.post(`/biddings/items/single/item/${this.id}`).then((r) => {
        if (r.data && r.data.length > 0) {
          this.data = r.data[0];
          this.data.item_price = this.realMaskII(Number(r.data[0].item_price || 0).toFixed(3));
          this.data.item_billing_price = this.realMaskII(Number(r.data[0].item_billing_price || 0).toFixed(3));
          this.data.item_amount = this.realMaskII(Number(r.data[0].item_amount || 0).toFixed(3));
          this.data.item_available = this.realMaskII(Number(r.data[0].item_available || 0).toFixed(3));
          this.data.item_total_bidding_price = Number(r.data[0].item_total_bidding_price || 0).toFixed(2);
        }
        this.$store.dispatch("setLoader", false);
        setTimeout(() => {
          this.calcValue();
        }, 1000);
      }).catch(err => {
        console.error("Error loading item:", err);
        this.$store.dispatch("setLoader", false);
      });
    } else {
      this.data.bidding_id = Number(this.b_id);
    }
  },
  methods: {
    calcValue() {
      if (
        this.data.item_price &&
        this.data.item_amount &&
        parseFloat(this.data.item_price) > 0 &&
        parseFloat(this.data.item_amount) > 0
      ) {
        let item_price = this.convertDecimalNumberII(this.data.item_price);
        let item_amount = this.convertDecimalNumberII(this.data.item_amount);
        this.data.item_total_bidding_price = (
          parseFloat(item_price) * parseFloat(item_amount)
        ).toFixed(2);
      }
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    createOrUpdate() {
      this.loader = true;
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/biddings/items/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push(
                `/biddings-manager/edit/${this.data.bidding_id}`
              );
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.data.item_billing_price = this.data.item_price;
        this.$http
          .post("/biddings/items", this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            setTimeout(() => {
              this.$router.push(
                `/biddings-manager/edit/${this.data.bidding_id}`
              );
            }, 1500);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 4px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>